'use no memo';

import {
  forwardRef,
  useCallback,
  useRef,
  type ForwardedRef,
  type MutableRefObject,
  type ReactNode
} from 'react';

import { clsx } from 'clsx';

import {
  KibCheckFillIcon,
  KibInfoFillIcon,
  KibInfoOutlineIcon,
  KibWarningFillIcon
} from '@chewy/kib-icons-react';
import { KibTooltip } from '@chewy/kib-tooltip-react';

import './styles/styles.scss';
import type { ChirpTagProps, ChirpTagVariant } from './tag.types';
import { warnInvalidIconWithTooltip } from './tag.utils';

const DEFAULT_VARIANT = 'neutral';
const DEFAULT_SIZE = 'default';
const DEFAULT_EMPHASIS = 'primary';
const DEFAULT_ICON_POSITION = 'start';

/**
 * Default icons per variant. Used automatically by ChirpTag when `icon` is omitted.
 * Consumers can also call this helper when composing custom icon slots.
 *
 * @param variant - Semantic tag variant
 * @param ariaLabel - When set, the icon is exposed to assistive tech with this name
 *   instead of being marked decorative
 */
export const getDefaultVariantIcon = (variant: ChirpTagVariant, ariaLabel?: string): ReactNode => {
  const Icon =
    variant === 'success'
      ? KibCheckFillIcon
      : variant === 'caution' || variant === 'error'
      ? KibWarningFillIcon
      : KibInfoFillIcon;

  if (ariaLabel) {
    return <Icon aria-label={ariaLabel} focusable="false" role="img" />;
  }

  return <Icon aria-hidden="true" data-a11y="decorative" focusable="false" />;
};

const getTagClassName = ({
  variant,
  size,
  emphasis,
  className,
  withIcon,
  iconPosition,
  withTooltip
}: {
  variant: ChirpTagVariant;
  size: 'default' | 'small';
  emphasis: 'primary' | 'secondary';
  className?: string;
  withIcon?: boolean;
  iconPosition?: 'start' | 'end';
  withTooltip?: boolean;
}): string =>
  clsx(
    'chirp-tag',
    `chirp-tag--${variant}`,
    size === 'small' && 'chirp-tag--small',
    size !== 'small' && 'chirp-tag--default',
    emphasis === 'secondary' && 'chirp-tag--secondary',
    emphasis !== 'secondary' && 'chirp-tag--primary',
    withTooltip && 'chirp-tag--tooltip',
    withIcon && 'chirp-tag--with-icon',
    withIcon && iconPosition && `chirp-tag--icon-${iconPosition}`,
    className
  );

/**
 * A visual indicator used to highlight status or supply supplemental information.
 * Supports semantic variants, optional icons, and an integrated tooltip mode.
 */
export const ChirpTag = forwardRef<HTMLSpanElement | HTMLButtonElement, ChirpTagProps>(
  (props, ref) => {
    const hasTooltip = props.tooltipProps !== undefined;
    const hasCustomIconProps =
      'icon' in props || 'iconPosition' in props || 'iconAriaLabel' in props;

    // Always called: tooltip mode needs a stable MutableRefObject for KibTooltip.
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const setTriggerRef = useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        if (ref) {
          (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
        }
      },
      [ref]
    );

    warnInvalidIconWithTooltip(hasTooltip, hasCustomIconProps);

    if (hasTooltip) {
      const {
        label,
        variant = DEFAULT_VARIANT,
        size = DEFAULT_SIZE,
        emphasis = DEFAULT_EMPHASIS,
        className,
        tooltipProps,
        ...buttonProps
      } = props;

      return (
        <>
          <button
            ref={setTriggerRef}
            data-chirp={globalThis.__CHIRP_PACKAGE__}
            {...buttonProps}
            type="button"
            className={getTagClassName({
              variant,
              size,
              emphasis,
              className,
              withTooltip: true
            })}
          >
            <span className="chirp-tag__label-outer">
              <span className="chirp-tag__label">{label}</span>
            </span>
            <KibInfoOutlineIcon
              aria-hidden="true"
              className="chirp-tag__icon chirp-tag__icon--tooltip"
              data-a11y="decorative"
              focusable="false"
            />
          </button>
          <KibTooltip placement="bottom" {...tooltipProps} trigger={triggerRef} />
        </>
      );
    }

    const {
      label,
      variant = DEFAULT_VARIANT,
      size = DEFAULT_SIZE,
      emphasis = DEFAULT_EMPHASIS,
      className,
      icon,
      iconPosition = DEFAULT_ICON_POSITION,
      iconAriaLabel,
      tooltipProps,
      ...spanProps
    } = props;

    // Consume an explicitly undefined value instead of forwarding it to the span.
    void tooltipProps;

    // Omit (`undefined`) → default variant icon. Falsy (`null` / `false`) → hide.
    // Supports idiomatic `icon={condition && <Icon />}` without leaving an empty slot.
    const iconSource =
      icon === undefined ? getDefaultVariantIcon(variant, iconAriaLabel) : icon || null;
    const withIcon = Boolean(iconSource);

    return (
      <span
        ref={ref as ForwardedRef<HTMLSpanElement>}
        data-chirp={globalThis.__CHIRP_PACKAGE__}
        {...spanProps}
        className={getTagClassName({
          variant,
          size,
          emphasis,
          className,
          withIcon,
          iconPosition
        })}
      >
        {iconPosition === 'start' && withIcon ? (
          <span className="chirp-tag__icon">{iconSource}</span>
        ) : null}
        <span className="chirp-tag__label-outer">
          <span className="chirp-tag__label">{label}</span>
        </span>
        {iconPosition === 'end' && withIcon ? (
          <span className="chirp-tag__icon">{iconSource}</span>
        ) : null}
      </span>
    );
  }
);

ChirpTag.displayName = 'ChirpTag';
